import { plainPrompt } from "@seedance/contracts";
import type { Edge, Node } from "@xyflow/react";
import type { CanvasNodeV3 as CanvasNode } from "@seedance/contracts";

export type BenchmarkNodeKind =
  | "text"
  | "image"
  | "video"
  | "audio"
  | "generation"
  | "composition"
  | "group";

export type BenchmarkNodeData = {
  label: string;
  kind: BenchmarkNodeKind;
  mediaLoaded: boolean;
  accent: string;
};

export type BenchmarkNode = Node<
  BenchmarkNodeData,
  "default" | "media"
>;

export const benchmarkPreviewLimit = 50;

export interface BenchmarkProductionNodeData extends Record<string, unknown> {
  canvasNode: CanvasNode;
  label: string;
  accent: string;
  previewEnabled: boolean;
  previewUrl?: string;
  contentUrl: string | null;
  referenceCounts: { image: number; video: number; audio: number };
}

export type BenchmarkProductionNode = Node<
  BenchmarkProductionNodeData,
  CanvasNode["kind"]
>;

const nodeKinds: BenchmarkNodeKind[] = [
  "text",
  "image",
  "video",
  "audio",
  "generation",
  "composition",
  "group"
];

const accents: Record<BenchmarkNodeKind, string> = {
  text: "#7c6ff2",
  image: "#31a8ff",
  video: "#ff6b8a",
  audio: "#36c98f",
  generation: "#f59e4b",
  composition: "#b06df2",
  group: "#748094"
};

const mediaKinds = new Set<BenchmarkNodeKind>(["image", "video", "audio"]);
const kindLabels: Record<BenchmarkNodeKind, string> = {
  text: "提示词",
  image: "图片素材",
  video: "视频素材",
  audio: "音频素材",
  generation: "视频生成",
  composition: "合成导出",
  group: "分组"
};

export function buildBenchmarkGraph(nodeCount: number, edgeCount: number): {
  nodes: BenchmarkNode[];
  edges: Edge[];
} {
  if (!Number.isInteger(nodeCount) || nodeCount < 2) {
    throw new Error("Benchmark graph requires at least two nodes.");
  }
  if (!Number.isInteger(edgeCount) || edgeCount < 0) {
    throw new Error("Benchmark edge count must be a non-negative integer.");
  }

  const nodes: BenchmarkNode[] = Array.from(
    { length: nodeCount },
    (_, index) => {
      const kind = nodeKinds[index % nodeKinds.length]!;
      return {
        id: `node-${index}`,
        type: mediaKinds.has(kind) ? "media" : "default",
        width: mediaKinds.has(kind) ? 190 : 180,
        height: mediaKinds.has(kind) ? 118 : 70,
        position: {
          x: (index % 25) * 260,
          y: Math.floor(index / 25) * 180
        },
        data: {
          label: `${kindLabels[kind]} · ${index + 1}`,
          kind,
          mediaLoaded: false,
          accent: accents[kind]
        },
        ...(!mediaKinds.has(kind)
          ? {
              style: {
              width: 180,
              minHeight: 70,
              color: "#f7f8fc",
              background: "#171b24",
              border: `1px solid ${accents[kind]}88`,
              borderRadius: 14,
              boxShadow: "0 10px 26px rgba(0, 0, 0, 0.28)",
              padding: 14
            }
            }
          : {})
      };
    }
  );
  const edges: Edge[] = Array.from({ length: edgeCount }, (_, index) => {
    const sourceIndex = index % nodeCount;
    let targetIndex = (index * 17 + 1) % nodeCount;
    if (targetIndex === sourceIndex) {
      targetIndex = (targetIndex + 1) % nodeCount;
    }
    return {
      id: `edge-${index}`,
      source: `node-${sourceIndex}`,
      target: `node-${targetIndex}`
    };
  });

  return { nodes, edges };
}

function syntheticThumbnail(accent: string): string {
  return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="220" height="112"><rect width="100%" height="100%" fill="${accent}"/><path d="M0 84L55 40l38 32 38-46 89 58v28H0z" fill="#10141b" fill-opacity=".65"/></svg>`)}`;
}

function productionKind(kind: BenchmarkNodeKind): CanvasNode["kind"] {
  if (kind === "generation") return "seedance";
  if (kind === "composition" || kind === "group") return "text";
  return kind;
}

function productionCanvasNode(
  node: BenchmarkNode,
  kind: CanvasNode["kind"]
): CanvasNode {
  const size = { width: node.width ?? 180, height: node.height ?? 70 };
  const common = {
    id: node.id,
    position: node.position,
    size,
    collapsed: false
  };
  if (kind === "image" || kind === "video" || kind === "audio") {
    return { ...common, kind, assetId: `benchmark-asset-${node.id}` };
  }
  if (kind === "text") {
    return { ...common, kind, text: plainPrompt(node.data.label) };
  }
  return {
    ...common,
    kind: "seedance",
    referenceBindings: [],
    settings: {
      prompt: plainPrompt(node.data.label),
      model: "doubao-seedance-2-0-260128",
      ratio: "16:9",
      durationSeconds: 5,
      resolution: "1080p",
      watermark: false,
      generateAudio: false,
      seed: -1
    }
  };
}

export function buildProductionBenchmarkGraph(nodeCount: number, edgeCount: number): {
  nodes: BenchmarkProductionNode[];
  edges: Edge[];
} {
  const graph = buildBenchmarkGraph(nodeCount, edgeCount);
  let previewCount = 0;
  const nodes = graph.nodes.map((node) => {
    const kind = productionKind(node.data.kind);
    const isMedia = kind === "image" || kind === "video" || kind === "audio";
    const previewEnabled = isMedia && previewCount < benchmarkPreviewLimit;
    if (previewEnabled) previewCount += 1;
    return {
      id: node.id,
      type: kind,
      position: node.position,
      width: node.width ?? 180,
      height: node.height ?? 70,
      data: {
        canvasNode: productionCanvasNode(node, kind),
        label: node.data.label,
        accent: node.data.accent,
        previewEnabled,
        ...(previewEnabled ? { previewUrl: syntheticThumbnail(node.data.accent) } : {}),
        contentUrl: null,
        referenceCounts: { image: 0, video: 0, audio: 0 }
      }
    };
  });
  // Seedance consumes inputs but does not mount a source handle. Keep the
  // benchmark on real production handles without duplicating source/target pairs.
  const sources = nodes.flatMap((node,index) => node.type === "seedance" ? [] : [index]);
  if (edgeCount > sources.length * (nodes.length - 1)) {
    throw new Error("Requested too many unique production edges.");
  }
  const edges: Edge[] = Array.from({length:edgeCount},(_,index) => {
    const sourceIndex = sources[index % sources.length]!;
    const targetOffset = Math.floor(index / sources.length) + 1;
    return {
      id:`edge-${index}`,
      source:nodes[sourceIndex]!.id,
      target:nodes[(sourceIndex + targetOffset) % nodes.length]!.id
    };
  });
  return { nodes, edges };
}
